import { paddingToPixelString } from '@topcon/tuicl/dist/common/utils';
import {
  TeButton,
  TeContainer,
  TeContainerSlim,
  TeDropdown,
  TeDropdownOption,
  TePagination,
  TeSearch,
  TeText,
  TeTooltip,
} from '@topcon/tuicl-react';
import styled from 'styled-components';
import { Dispatch, ReactNode, SetStateAction, useEffect, useState } from 'react';
import { TePageChangeEvent } from '@topcon/tuicl';

import { useMediaQuery } from '../hooks/useMediaQuery';
import { border } from '../providers/theme/theme.index';
import { BREAKPOINTS } from '../utils/breakpoints';

const PAGINATION_LIMITS = ['10', '20', '30', '40', '50'] as const;
const DEFAULT_SEARCH_DELAY = 400;

export interface PaginationConfig {
  enabled: boolean;
  pages: string;
  page: string;
  onPageChange: (next: string) => void;
  onLimitChange?: (limit: (typeof PAGINATION_LIMITS)[number]) => void;
  size?: 's' | 'm';
  compact?: boolean;
  limit?: (typeof PAGINATION_LIMITS)[number];
}

interface Props {
  title: string;
  children: ReactNode;
  searchValue?: string;
  setSearchValue?: Dispatch<SetStateAction<string>>;
  searchPlaceholder?: string;
  visibleItemsText?: string | undefined;
  visibleItemsCount?: number;
  createNewItemText?: string;
  onCreateNewItemClick?: () => void;
  buttonDisabled?: boolean;
  tooltipText?: string;
  noPadding?: boolean;
  noSeparator?: boolean;
  overflow?: 'auto' | 'hidden' | 'visible';
  contentOverflow?: 'auto' | 'hidden' | 'visible';
  pagination?: PaginationConfig;
  isLoadingData?: boolean;
  debounceSearch?: boolean;
  compactCreateLabel?: boolean;
}

function ViewContainer({
  title,
  children,
  searchValue,
  setSearchValue,
  searchPlaceholder,
  visibleItemsText,
  visibleItemsCount,
  createNewItemText,
  onCreateNewItemClick,
  buttonDisabled,
  tooltipText,
  noPadding,
  noSeparator,
  contentOverflow = 'hidden',
  pagination,
  isLoadingData,
  debounceSearch,
  compactCreateLabel,
}: Props) {
  const isTablet = useMediaQuery(`(max-width: ${BREAKPOINTS.tablet})`);
  const [localSearchValue, setLocalSearchValue] = useState<string>(() => searchValue ?? '');

  useEffect(() => {
    setLocalSearchValue(searchValue ?? '');
  }, [searchValue]);

  useEffect(() => {
    if (!setSearchValue) return;

    if (!debounceSearch) {
      setSearchValue(localSearchValue);
      return;
    }

    const timeoutId = setTimeout(() => {
      setSearchValue(localSearchValue);
    }, DEFAULT_SEARCH_DELAY);

    return () => clearTimeout(timeoutId);
  }, [debounceSearch, localSearchValue, setSearchValue]);

  return (
    <TeContainer
      justifyContent="center"
      backgroundColor="background.tertiary"
      padding={noPadding ? 0 : 'xs'}
      relative
      grow
      fill
      column
      gap={0}
      borderRadius={0}
    >
      <ContentWrapper>
        <TeContainerSlim column grow gap="xs" relative>
          {isTablet ? (
            <TeContainer
              column
              gap="xs"
              padding={['s', 'm']}
              backgroundColor="background.primary"
              borderRadius={4}
              fill
              overflow="auto"
            >
              <TeContainerSlim alignItems="center" justifyContent="space-between" gap="xs" fill>
                <TeText size={20} weight={700}>
                  {title}
                </TeText>

                <MobileActions gap="xs" justifyContent="flex-end" alignItems="center">
                  {createNewItemText && (
                    <TeButton
                      icon="add"
                      size="s"
                      teclick={onCreateNewItemClick}
                      disabled={buttonDisabled}
                      title={createNewItemText}
                      text={!compactCreateLabel ? createNewItemText : ''}
                      aria-label={createNewItemText}
                    />
                  )}
                </MobileActions>
              </TeContainerSlim>

              {setSearchValue && (
                <TeSearch
                  size="s"
                  placeholder={searchPlaceholder}
                  value={localSearchValue}
                  techange={(e) => {
                    setLocalSearchValue(e.currentTarget.value);
                  }}
                  clearable={!!localSearchValue}
                  disabled={isLoadingData}
                  grow
                />
              )}
            </TeContainer>
          ) : (
            <TeContainer
              overflow="auto"
              justifyContent="space-between"
              backgroundColor="background.primary"
              borderRadius={4}
              fill
              height="auto"
            >
              <TeContainerSlim alignItems="center" gap="xs" justifyContent="flex-start">
                <TeText size={20} weight={700}>
                  {title}
                </TeText>
                {visibleItemsText && (
                  <>
                    {!noSeparator && <Divider />}
                    <TeText size={14} color="text.secondary">
                      {visibleItemsText}
                    </TeText>
                  </>
                )}

                {visibleItemsCount !== undefined && visibleItemsCount !== 0 && (
                  <TeContainerSlim gap="xs3" aria-label="visible-items-count">
                    <TeText size={14} color="text.secondary">
                      (
                    </TeText>
                    <TeText size={14} color="text.secondary">
                      {visibleItemsCount}
                    </TeText>
                    <TeText size={14} color="text.secondary">
                      )
                    </TeText>
                  </TeContainerSlim>
                )}
              </TeContainerSlim>

              <ResponsiveActions alignItems="center" gap="s" justifyContent="flex-start">
                {setSearchValue && (
                  <TeSearch
                    size="s"
                    placeholder={searchPlaceholder}
                    value={localSearchValue}
                    techange={(e) => {
                      setLocalSearchValue(e.currentTarget.value);
                    }}
                    clearable={!!localSearchValue}
                    disabled={isLoadingData}
                  />
                )}

                {createNewItemText && (
                  <TeContainerSlim popkey="createNewButton">
                    <TeButton
                      icon="add"
                      text={createNewItemText}
                      size="s"
                      teclick={onCreateNewItemClick}
                      disabled={buttonDisabled}
                      grow
                    />
                    {tooltipText && (
                      <TeTooltip
                        for="createNewButton"
                        variant="secondary"
                        text={tooltipText}
                        placement="bottom"
                      />
                    )}
                  </TeContainerSlim>
                )}
              </ResponsiveActions>
            </TeContainer>
          )}

          <TeContainerSlim
            overflow={contentOverflow}
            column
            grow
            borderRadius={['m', 'm', pagination?.enabled ? 0 : 'm', pagination?.enabled ? 0 : 'm']}
            relative
          >
            {children}
          </TeContainerSlim>
        </TeContainerSlim>
      </ContentWrapper>
      {pagination && pagination?.enabled && (
        <PaginationWrapper
          backgroundColor="background.primary"
          padding={['xs', 'm']}
          borderRadius={[0, 0, 'm', 'm']}
          fill
          $noPadding={noPadding}
          justifyContent="space-between"
          alignItems="center"
        >
          <TeDropdown
            size="s"
            style={{ width: 70 }}
            valueByDefault={pagination.limit ?? PAGINATION_LIMITS[0]}
            persistent
            disabled={isLoadingData}
          >
            {PAGINATION_LIMITS.map((limit) => (
              <TeDropdownOption
                key={limit}
                text={limit}
                value={limit}
                teclick={() => pagination.onLimitChange && pagination.onLimitChange(limit)}
              />
            ))}
          </TeDropdown>
          <TePagination
            style={{
              opacity: isLoadingData ? 0.75 : 1,
              pointerEvents: isLoadingData ? 'none' : 'auto',
            }}
            size={pagination.size ?? 's'}
            compact={pagination.compact ?? false}
            pages={Number(pagination.pages)}
            page={Number(pagination.page)}
            blockInteraction={isLoadingData}
            techange={(e: TePageChangeEvent) => pagination.onPageChange(e.page.toString())}
          />
        </PaginationWrapper>
      )}
    </TeContainer>
  );
}

export default ViewContainer;

const Divider = styled.div<{ $height?: number }>`
  height: ${(props) => props.$height || 16}px;
  width: 1px;
  background: ${border.primary};
`;

const ContentWrapper = styled.div`
  height: 100%;
  max-height: 100%;
  width: 100%;
  display: flex;
  justify-content: center;
`;

const PaginationWrapper = styled(TeContainerSlim)<{ $noPadding?: boolean }>`
  border-top: 1px solid ${border.secondary} !important;
  border-right: 0 !important;
  border-bottom: 0 !important;
  border-left: 0 !important;
  width: ${({ $noPadding }) => `calc(100% - ${paddingToPixelString($noPadding ? 0 : 'xs')} * 2)`};
`;

const ResponsiveActions = styled(TeContainerSlim)`
  flex-wrap: wrap;
`;

const MobileActions = styled(TeContainerSlim)`
  flex-shrink: 0;
`;
